import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import {
  buildEscapeUrl,
  buildAndroidChromeIntent,
  inAppBrowserDisplayName,
  navigateToEscapeUrl,
  trackInAppBrowserEscape,
} from "../../utils/in-app-browser";
import { QLINK_FUNNEL_EVENT, inAppFunnelProps, recordQLinkFunnelEvent } from "../../utils/qlink-funnel-events";
import { REPLAY_MASK } from "../../utils/replay-mask";
import "./inapp-escape-panel.scss";

const readShareToken = () => {
  try {
    return window.localStorage.getItem("userAcquisitiontoken") || "";
  } catch (e) {
    // Storage blocked: build the escape link without a token (the panel says to reopen the post).
    return "";
  }
};

// Shown on the drawer's Google tab inside a social in-app browser, where Google
// refuses OAuth (CONTRACT-FIX1). Android: open the question in Chrome through
// an intent. iOS/other: copy the link to paste into Safari. The tokenized URL
// is built only at click time and handed to the clipboard or a navigation; it
// is never rendered into an href (GA4 link-click listeners and session replay
// read the DOM). The by-hand fallback field is masked for Hotjar and Clarity.
// qlinkId/questionId feed only the first-party inapp_escape_clicked event.
// The sign-in page (/login) shows the same panel on its Google tab with its
// own `lead` (the opening sentence) and `buildTargetUrl` (the page's own
// address, no share token); without them, the question's wording and its
// share token as before. /login also passes `onUsePhone` (a first, primary
// "Use phone number" button that switches it to its Phone tab) and
// `offerAppMenu`: an app's own menu "Open in browser" opens the address-bar
// URL, which on /login already carries ?next= and the campaign keys (and no
// token). Hedged ("some apps"): the item's name and place vary by app, and
// some webviews have no menu. The drawer passes neither: its address bar
// lacks the share token.
const InAppEscapePanel = ({
  inAppBrowser, platform, onTryGoogleAnyway, qlinkId, questionId,
  lead, buildTargetUrl, onUsePhone, offerAppMenu = false,
}) => {
  // 'idle' | 'copied' | 'fallback' (clipboard refused: the link to copy by hand)
  const [copyState, setCopyState] = useState("idle");
  const [fallbackUrl, setFallbackUrl] = useState("");
  const shownTrackedRef = useRef(false);
  const { app, os } = inAppBrowser;
  const appName = inAppBrowserDisplayName(app);
  // A shared question's escape carries its share token; a caller's own target never does.
  const isShareLink = !buildTargetUrl;
  const buildTarget = () => (isShareLink ? buildEscapeUrl({ shareToken: readShareToken() }) : buildTargetUrl());
  let browserName = "your browser";
  if (os === "android") browserName = "Chrome";
  else if (os === "ios") browserName = "Safari";
  const addressBar = browserName === "your browser" ? "the address bar" : `${browserName}'s address bar`;
  // How the app's menu button usually looks; after the word "menu" and hidden
  // from screen readers, which say "menu" only.
  let menuGlyph = "";
  if (os === "ios") menuGlyph = "•••";
  else if (os === "android") menuGlyph = "⋮";

  useEffect(() => {
    if (!shownTrackedRef.current) {
      shownTrackedRef.current = true;
      trackInAppBrowserEscape({ app, os, action: "shown", platform });
    }
  }, [app, os, platform]);

  const recordEscapeClick = (action) => recordQLinkFunnelEvent({
    qlinkId,
    questionId,
    platform,
    event: QLINK_FUNNEL_EVENT.INAPP_ESCAPE_CLICKED,
    props: { action, ...inAppFunnelProps(inAppBrowser) },
  });

  // The dataLayer event only: no QLink funnel event (the panel's Phone action
  // is /login's alone).
  const onPhoneClick = () => {
    trackInAppBrowserEscape({ app, os, action: "phone_clicked", platform });
    onUsePhone();
  };

  const onOpenInChrome = () => {
    trackInAppBrowserEscape({ app, os, action: "escape_clicked", platform });
    recordEscapeClick("escape_clicked");
    const intent = buildAndroidChromeIntent(buildTarget());
    if (intent) {
      navigateToEscapeUrl(intent);
    }
  };

  const onCopyLink = () => {
    trackInAppBrowserEscape({ app, os, action: "copy_clicked", platform });
    recordEscapeClick("copy_clicked");
    const escapeUrl = buildTarget();
    const showFallback = () => {
      setFallbackUrl(escapeUrl);
      setCopyState("fallback");
    };
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") {
        showFallback();
        return;
      }
      navigator.clipboard.writeText(escapeUrl)
        .then(() => setCopyState("copied"))
        .catch(showFallback);
    } catch {
      // Clipboard API threw synchronously (permissions policy): show the link to copy by hand.
      showFallback();
    }
  };

  return (
    <Box className="inapp-escape">
      <Typography className="inapp-escape-copy">
        {lead || `Google sign-in doesn't work inside ${appName}. Open this question in ${browserName} to use Google, or use Phone sign-in.`}
      </Typography>
      {onUsePhone && (
        <Button variant="contained" color="primary" className="inapp-escape-btn inapp-escape-phone" onClick={onPhoneClick}>
          Use phone number
        </Button>
      )}
      {os === "android" && (
        <Button variant="outlined" color="secondary" className="inapp-escape-btn" onClick={onOpenInChrome}>
          Open in Chrome
        </Button>
      )}
      {/* Also on Android: some apps ignore intent:// links. */}
      <Button variant="outlined" color="secondary" className="inapp-escape-btn" onClick={onCopyLink}>
        Copy link
      </Button>
      {copyState === "copied" && (
        <Typography className="inapp-escape-status" role="status">
          Link copied. Paste it into {browserName}.
        </Typography>
      )}
      {copyState === "fallback" && (
        <input
          className="inapp-escape-fallback"
          aria-label={`Link to open in ${browserName}`}
          readOnly
          value={fallbackUrl}
          onFocus={(e) => e.target.select()}
          {...REPLAY_MASK}
          data-clarity-mask="true"
        />
      )}
      {/* The drawer never points at the app's own "Open in browser": that
          opens the address-bar URL, which no longer carries the share token
          (a dead page there). */}
      <Typography className="inapp-escape-tip">
        {offerAppMenu ? (
          <>
            {`Then paste it into ${addressBar}. Some apps also have Open in browser in their menu`}
            {menuGlyph && <span aria-hidden="true">{` (${menuGlyph})`}</span>}
            .
          </>
        ) : `Then open ${browserName} and paste it into ${addressBar}.`}
      </Typography>
      {onTryGoogleAnyway && (
        <Button size="small" className="inapp-escape-anyway" onClick={onTryGoogleAnyway}>
          Try Google sign-in anyway
        </Button>
      )}
      {isShareLink && !readShareToken() && (
        <Typography className="inapp-escape-tip">
          If the page says the link expired, reopen the original post.
        </Typography>
      )}
    </Box>
  );
};

export default InAppEscapePanel;
